export const diasSemana = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
];

export const defaultHorarios = diasSemana.map((_, dia_semana) => ({
  dia_semana,
  abre: '09:00',
  fecha: '18:00',
  fechado: false,
}));

export const parseTimeToMinutes = (value) => {
  if (!value) return null;
  const parts = String(value).split(':');
  if (parts.length < 2) return null;
  const horas = Number.parseInt(parts[0], 10);
  const minutos = Number.parseInt(parts[1], 10);
  if (Number.isNaN(horas) || Number.isNaN(minutos)) return null;
  if (horas < 0 || horas > 23 || minutos < 0 || minutos > 59) return null;
  return horas * 60 + minutos;
};

export const parseOrNull = (value, parser) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = parser(value);
  return Number.isNaN(parsed) ? NaN : parsed;
};

export const normalizeRestauranteComparable = (source = {}) => ({
  lat: parseOrNull(source.lat, parseFloat),
  long: parseOrNull(source.long, parseFloat),
  raio: parseOrNull(source.raio, (value) => parseInt(value, 10)),
  intervalo_checkin: parseOrNull(source.intervalo_checkin, (value) => parseInt(value, 10)),
});

export const normalizePrincipalAddressComparable = (source = {}) => ({
  logradouro: String(source?.logradouro || '').trim(),
  numero: String(source?.numero || '').trim(),
  complemento: String(source?.complemento || '').trim(),
  bairro: String(source?.bairro || '').trim(),
  cidade: String(source?.cidade || '').trim(),
  estado: String(source?.estado || '').trim().toUpperCase(),
  cep: String(source?.cep || '').replace(/\D/g, ''),
  place_id: String(source?.place_id || '').trim(),
  formatted_address: String(source?.formatted_address || '').trim(),
});

export const isPrincipalAddressComparableEqual = (a, b) => (
  a?.logradouro === b?.logradouro
  && a?.numero === b?.numero
  && a?.complemento === b?.complemento
  && a?.bairro === b?.bairro
  && a?.cidade === b?.cidade
  && a?.estado === b?.estado
  && a?.cep === b?.cep
  && a?.place_id === b?.place_id
  && a?.formatted_address === b?.formatted_address
);

export const normalizeHorarioComparable = (horario = {}) => {
  const fechado = Boolean(horario?.fechado);
  return {
    dia_semana: Number(horario?.dia_semana ?? 0),
    fechado,
    abre: fechado ? '' : (horario?.abre ? String(horario.abre).slice(0, 5) : ''),
    fecha: fechado ? '' : (horario?.fecha ? String(horario.fecha).slice(0, 5) : ''),
  };
};

export const buildHorarioComparableMap = (horariosData = []) => {
  const map = new Map();
  (horariosData || []).forEach((horario) => {
    if (!horario?.id) return;
    map.set(String(horario.id), normalizeHorarioComparable(horario));
  });
  return map;
};

export const isHorarioComparableEqual = (a, b) => (
  a?.dia_semana === b?.dia_semana
  && a?.fechado === b?.fechado
  && a?.abre === b?.abre
  && a?.fecha === b?.fecha
);

export const buildHorarioPayload = (horario = {}) => {
  const fechado = Boolean(horario?.fechado);
  return {
    dia_semana: Number(horario?.dia_semana ?? 0),
    abre: fechado ? null : (horario?.abre ? String(horario.abre).slice(0, 5) : null),
    fecha: fechado ? null : (horario?.fecha ? String(horario.fecha).slice(0, 5) : null),
    fechado,
  };
};

const normalizeHorario = (horario) => ({
  ...horario,
  dia_semana: Number(horario?.dia_semana ?? 0),
  abre: horario?.abre ? horario.abre.slice(0, 5) : '',
  fecha: horario?.fecha ? horario.fecha.slice(0, 5) : '',
  fechado: Boolean(horario?.fechado),
});

export const buildHorariosState = (horariosData = []) => {
  const agrupados = new Map();
  (horariosData || []).forEach((horario) => {
    const normalizado = normalizeHorario(horario);
    const dia = normalizado.dia_semana;
    if (!agrupados.has(dia)) agrupados.set(dia, []);
    agrupados.get(dia).push(normalizado);
  });

  const base = [];
  const extras = [];

  for (let dia = 0; dia < 7; dia += 1) {
    const lista = agrupados.get(dia) || [];
    if (lista.length > 0) {
      lista.sort((a, b) => (parseTimeToMinutes(a.abre) ?? 0) - (parseTimeToMinutes(b.abre) ?? 0));
      const [principal, ...resto] = lista;
      base.push({ ...principal, dia_semana: dia });
      resto.forEach((item) => extras.push({ ...item, dia_semana: dia }));
    } else {
      base.push({ ...defaultHorarios[dia] });
    }
  }

  return { base, extras };
};

export const emptyLocalidade = {
  id: null,
  nome: '',
  lat: '',
  long: '',
  raio: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  place_id: '',
  formatted_address: '',
};

export const extractApiErrorMessage = (error, fallbackMessage) => {
  const apiMessage = error?.response?.data?.message
    || error?.response?.data?.error
    || error?.response?.data?.details?.message;
  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return apiMessage;
  }
  return fallbackMessage;
};

export const formatValue = (value, suffix = '') => (
  value !== null && value !== undefined && value !== '' ? `${value}${suffix}` : '-'
);

export const parsePercent = (value) => (parseFloat(value) || 0) / 100;

export const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = src;
});

export const validateHorarios = (lista, dayLabels = diasSemana) => {
  const porDia = new Map();
  lista.forEach((horario) => {
    if (horario.fechado) return;
    const dia = Number(horario.dia_semana ?? 0);
    if (!porDia.has(dia)) porDia.set(dia, []);
    porDia.get(dia).push(horario);
  });

  for (let dia = 0; dia < 7; dia += 1) {
    const itens = porDia.get(dia) || [];
    if (itens.length === 0) continue;

    const segmentos = [];

    for (const horario of itens) {
      const inicio = parseTimeToMinutes(horario.abre);
      const fim = parseTimeToMinutes(horario.fecha);

      if (inicio === null || fim === null) {
        return {
          ok: false,
          message: `Preencha horários válidos para ${dayLabels[dia]}.`,
        };
      }

      if (inicio >= fim) {
        return {
          ok: false,
          message: `Horário inválido para ${dayLabels[dia]}. O início deve ser menor que o fim.`,
        };
      }

      segmentos.push({ start: inicio, end: fim });
    }

    segmentos.sort((a, b) => a.start - b.start);
    for (let i = 1; i < segmentos.length; i += 1) {
      if (segmentos[i].start < segmentos[i - 1].end) {
        return {
          ok: false,
          message: `Os horários de ${dayLabels[dia]} estão sobrepostos.`,
        };
      }
    }
  }

  return { ok: true, message: '' };
};

export const isDiaFechado = (horariosBase, dia) => {
  const base = (horariosBase || []).find((h) => h.dia_semana === dia);
  return Boolean(base?.fechado);
};

export const buildHorariosResumo = (horariosBase = [], horariosExtras = []) => (
  [...horariosBase, ...horariosExtras].sort((a, b) => {
    if (a.dia_semana !== b.dia_semana) return a.dia_semana - b.dia_semana;
    return (parseTimeToMinutes(a.abre) ?? 0) - (parseTimeToMinutes(b.abre) ?? 0);
  })
);

export const buildExtrasPorDia = (horariosExtras = []) => (
  horariosExtras.reduce((acc, horario, index) => {
    const dia = Number(horario.dia_semana ?? 0);
    if (!acc[dia]) acc[dia] = [];
    acc[dia].push({ ...horario, _index: index });
    return acc;
  }, {})
);

export const buildClubeScheduleDays = (
  horariosBase = [],
  extrasPorDia = {},
  dayLabels = diasSemana,
) => horariosBase.map((horario) => ({
  day: String(horario.dia_semana),
  label: dayLabels[horario.dia_semana],
  closed: Boolean(horario.fechado),
  periods: horario.fechado
    ? []
    : [
        { start: horario.abre || '', end: horario.fecha || '' },
        ...(extrasPorDia[horario.dia_semana] || []).map((extra) => ({
          start: extra.abre || '',
          end: extra.fecha || '',
        })),
      ],
}));

export const buildHorariosPorDia = (
  horariosResumo = [],
  horariosBase = [],
  dayLabels = diasSemana,
) => dayLabels.map((label, dia) => {
  const intervalos = horariosResumo.filter((h) => h.dia_semana === dia && !h.fechado);
  const fechado = isDiaFechado(horariosBase, dia) && intervalos.length === 0;
  const hasExtras = intervalos.length > 1;
  return { dia, label, intervalos, fechado, hasExtras };
});
